import LogRocket from "logrocket";
import { IAuth } from "src/defs";
import type { UserProfile } from "src/services/types";

const isEnabled = () => Boolean(process.env.REACT_APP_LOGROCKET_APP_ID);

let currentUid: string | undefined;
let currentTenantId: string | undefined;
let currentEmail: string | undefined;
let currentClientId: string | undefined;
let lastIdentifiedTraits: string | undefined;

// `identify` attaches traits to the session that is already recording, so the
// session id survives; only `startNewSession` rotates it.
function identify(): void {
  if (!currentUid) {
    return;
  }

  const traits: Record<string, string> = {};
  if (currentTenantId) {
    traits.gcpTenantId = currentTenantId;
  }
  if (currentEmail) {
    traits.email = currentEmail;
  }
  if (currentClientId) {
    traits.clientId = currentClientId;
  }

  const traitsKey = `${currentUid}|${JSON.stringify(traits)}`;
  if (traitsKey === lastIdentifiedTraits) {
    return;
  }
  lastIdentifiedTraits = traitsKey;

  if (Object.keys(traits).length > 0) {
    LogRocket.identify(currentUid, traits);
  } else {
    LogRocket.identify(currentUid);
  }
}

export function logRocketSyncUser(newAuth: IAuth.IUser | undefined): void {
  if (!isEnabled()) {
    return;
  }

  if (!newAuth?.uid) {
    if (currentUid) {
      LogRocket.startNewSession();
    }
    currentUid = undefined;
    currentTenantId = undefined;
    currentEmail = undefined;
    currentClientId = undefined;
    lastIdentifiedTraits = undefined;
    return;
  }

  if (currentUid && currentUid !== newAuth.uid) {
    currentEmail = undefined;
    currentClientId = undefined;
  }

  currentUid = newAuth.uid;
  currentTenantId = newAuth.gcpTenantId;
  identify();
}

export function logRocketSyncUserProfile(
  userProfile: UserProfile | undefined,
  clientId?: string
): void {
  if (!isEnabled()) {
    return;
  }

  currentEmail = userProfile?.email || userProfile?.userInformation?.emailId;
  currentClientId = clientId || undefined;

  // Account switch clears the profile while auth is still present. Skip
  // identify so we do not rewrite the session with a stripped trait set.
  if (!userProfile) {
    return;
  }

  identify();
}

export function logRocketCaptureException(
  e: unknown,
  extra?: Record<string, string | number | boolean>
) {
  if (!isEnabled() || e == null) {
    return;
  }
  const err = e instanceof Error ? e : new Error(String(e));
  try {
    LogRocket.captureException(err, extra ? { extra } : undefined);
  } catch {
    // ignore
  }
}
